/**
 * checkoutCalculations.js
 * Pure functions for checkout totals, discounts, tips, and staff splits.
 * No side effects, no API calls, no state mutation.
 */

// Payment type constants (match backend values)
export const PAYMENT_TYPES = {
  APP: 1,
  CARD: 2,
  CASH: 3,
  OTHER: 4,
  CARD_READER: 5,
  CARD_ON_FILE: 10,
  VOUCHER: 11,
  PAYMENT_LINK: 12,
  KLARNA: 13,
  CLEARPAY: 14,
  TAP_TO_PAY: 15,
  LOYALTY_REWARD: 16,
};

/**
 * Payments that go through Beu payments and split at the till: the stylist's
 * share and the salon's share land in the right accounts as the client pays.
 * Everything else (cash, other card machines, Other) is tracked and settled
 * in Finance > Commissions. One list, so the checkout card and tile tags agree.
 */
export const SPLIT_AT_TILL_TYPES = [
  PAYMENT_TYPES.CARD_READER,
  PAYMENT_TYPES.TAP_TO_PAY,
  PAYMENT_TYPES.PAYMENT_LINK,
  PAYMENT_TYPES.KLARNA,
  PAYMENT_TYPES.CLEARPAY,
  PAYMENT_TYPES.CARD_ON_FILE,
  PAYMENT_TYPES.APP,
];

export const PAYMENT_TYPE_LABELS = {
  [PAYMENT_TYPES.APP]: 'Pay by App',
  [PAYMENT_TYPES.CARD]: 'Card',
  [PAYMENT_TYPES.CASH]: 'Cash',
  [PAYMENT_TYPES.OTHER]: 'Other',
  [PAYMENT_TYPES.CARD_READER]: 'Card Reader',
  [PAYMENT_TYPES.CARD_ON_FILE]: 'Card on File',
  [PAYMENT_TYPES.VOUCHER]: 'Voucher',
  [PAYMENT_TYPES.PAYMENT_LINK]: 'Payment Link',
  [PAYMENT_TYPES.KLARNA]: 'Klarna',
  [PAYMENT_TYPES.CLEARPAY]: 'Clearpay',
  [PAYMENT_TYPES.TAP_TO_PAY]: 'Tap to Pay',
  [PAYMENT_TYPES.LOYALTY_REWARD]: 'Loyalty reward',
};

export const WALKIN_CLIENT_ID = '64f5c7789a069cda0188fea3';

/**
 * Round to 2 decimal places (currency precision).
 */
export function round2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Format a number as currency string.
 * @param {number} amount
 * @param {string} symbol - e.g. '£'
 * @returns {string}
 */
// Always pounds and pence (£48.00, £0.00), never £48 or £NaN
export function formatCurrency(amount, symbol = '£') {
  const n = Number(amount);
  const safe = Number.isFinite(n) ? n : 0;
  const formatted = Math.abs(safe).toFixed(2);
  return safe < 0 ? `-${symbol}${formatted}` : `${symbol}${formatted}`;
}

/**
 * Calculate the discount amount for a single line item.
 * @param {Object} item - { price, discount: { type, value } | null }
 * @returns {number} discount amount in £
 */
export function calcItemDiscount(item) {
  if (!item.discount) return 0;
  const { type, value } = item.discount;
  if (type === 'percentage') return round2(item.originalPrice * (value / 100));
  if (type === 'fixed') return round2(Math.min(value, item.originalPrice));
  return 0;
}

/**
 * Calculate the effective price for a line item after its per-item discount.
 * @param {Object} item
 * @returns {number}
 */
export function calcItemEffectivePrice(item) {
  return round2(item.originalPrice - calcItemDiscount(item));
}

/**
 * Calculate the total for a line item (effective price x quantity).
 */
export function calcItemTotal(item) {
  return round2(calcItemEffectivePrice(item) * (item.quantity || 1));
}

/**
 * Calculate all checkout totals from line items, cart discount, transactions, and tip.
 * @param {Object} params
 * @param {Array} params.lineItems - [{ originalPrice, discount, ... }]
 * @param {Object} params.cartDiscount - { type, value } | null
 * @param {Array} params.transactions - [{ amount }] (new payments this session)
 * @param {Array} params.existingTransactions - [{ amount }] (deposits/prior)
 * @param {Object} params.tip - { amount }
 * @returns {Object} { subtotal, itemDiscountTotal, cartDiscountAmount, discountTotal, depositsApplied, totalDue, totalPaid, balanceRemaining }
 */
export function calculateTotals({ lineItems, cartDiscount, transactions, existingTransactions, tip, taxAmount }) {
  // Subtotal before any discounts (sum of original prices x quantity)
  const subtotal = round2(lineItems.reduce((sum, item) => sum + (item.originalPrice || 0) * (item.quantity || 1), 0));

  // Per-item discounts (discount per unit x quantity)
  const itemDiscountTotal = round2(lineItems.reduce((sum, item) => sum + calcItemDiscount(item) * (item.quantity || 1), 0));

  // Subtotal after item discounts
  const afterItemDiscounts = round2(subtotal - itemDiscountTotal);

  // Cart-level discount applied to the after-item-discount total
  let cartDiscountAmount = 0;
  if (cartDiscount && cartDiscount.type) {
    if (cartDiscount.type === 'percentage') {
      cartDiscountAmount = round2(afterItemDiscounts * (cartDiscount.value / 100));
    } else if (cartDiscount.type === 'fixed') {
      cartDiscountAmount = round2(Math.min(cartDiscount.value, afterItemDiscounts));
    }
  }

  const discountTotal = round2(itemDiscountTotal + cartDiscountAmount);
  const tipAmount = round2((tip && tip.amount) || 0);

  // Total due = subtotal - all discounts + tip
  // taxAmount: tax charged ON TOP (exclusive mode); 0 when inclusive or off.
  const totalDue = round2(subtotal - discountTotal + tipAmount + (taxAmount || 0));

  // Already paid (deposits + prior payments)
  const depositsApplied = round2(
    (existingTransactions || []).reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0)
  );

  // Paid this session
  const sessionPaid = round2(
    (transactions || []).reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0)
  );

  const totalPaid = round2(depositsApplied + sessionPaid);
  const balanceRemaining = round2(Math.max(0, totalDue - totalPaid));

  return {
    subtotal,
    itemDiscountTotal,
    cartDiscountAmount,
    discountTotal,
    tipAmount,
    depositsApplied,
    totalDue,
    totalPaid,
    balanceRemaining,
  };
}

/**
 * Calculate per-staff breakdown for commission and tip splitting.
 * Tips are split proportionally based on each staff member's share of the total.
 * @param {Array} lineItems - [{ staffId, staffName, originalPrice, discount }]
 * @param {Object} tip - { amount, type }
 * @returns {Array} [{ staffId, staffName, subtotal, tipShare }]
 */
export function calculateStaffBreakdown(lineItems, tip) {
  const byStaff = {};
  lineItems.forEach(item => {
    const key = item.staffId || 'unknown';
    if (!byStaff[key]) {
      byStaff[key] = { staffId: key, staffName: item.staffName || 'Unknown', items: [] };
    }
    byStaff[key].items.push(item);
  });

  const grandTotal = lineItems.reduce((sum, item) => sum + calcItemTotal(item), 0);
  const tipAmount = (tip && tip.amount) || 0;

  return Object.values(byStaff).map(({ staffId, staffName, items }) => {
    const staffSubtotal = round2(items.reduce((sum, item) => sum + calcItemTotal(item), 0));
    const proportion = grandTotal > 0 ? staffSubtotal / grandTotal : 0;
    return {
      staffId,
      staffName,
      subtotal: staffSubtotal,
      tipShare: round2(tipAmount * proportion),
    };
  });
}

/**
 * Validate a payment amount.
 * NOTE: This is client-side validation only. The backend MUST re-validate
 * all amounts before processing any payment.
 * @param {string|number} value - Raw input value
 * @param {number} maxAmount - Maximum allowed (usually balanceRemaining)
 * @returns {{ valid: boolean, amount: number, error: string|null }}
 */
export function validatePaymentAmount(value, maxAmount) {
  const parsed = parseFloat(value);
  if (isNaN(parsed) || !isFinite(parsed)) {
    return { valid: false, amount: 0, error: 'Please enter a valid amount' };
  }
  if (parsed <= 0) {
    return { valid: false, amount: 0, error: 'Amount must be greater than zero' };
  }
  if (parsed > 10000) {
    return { valid: false, amount: 0, error: 'Amount exceeds maximum of 10,000' };
  }
  if (maxAmount > 0 && parsed > round2(maxAmount + 0.01)) {
    return { valid: false, amount: 0, error: `Amount cannot exceed ${formatCurrency(maxAmount)}` };
  }
  return { valid: true, amount: round2(parsed), error: null };
}

/**
 * Effective unit price after BOTH per-item discount AND cart-level discount.
 * Cart discount is spread proportionally across all line items by share of
 * after-item-discount value, so commission downstream is calculated on what
 * the item was actually sold for.
 *
 * @param {Object} item                       - line item
 * @param {Object|null} cartDiscount          - { type: 'percentage'|'fixed', value }
 * @param {number} subtotalAfterItemDiscounts - sum of (afterItemDiscountPrice × qty) across all line items
 * @returns {number} unit price after all discounts
 */
export function effectiveUnitPriceAfterCartDiscount(item, cartDiscount, subtotalAfterItemDiscounts) {
  const afterItemDiscount = calcItemEffectivePrice(item);
  if (!cartDiscount || !cartDiscount.type) return afterItemDiscount;
  if (cartDiscount.type === 'percentage') {
    return round2(afterItemDiscount * (1 - cartDiscount.value / 100));
  }
  if (cartDiscount.type === 'fixed' && subtotalAfterItemDiscounts > 0) {
    const cap = Math.min(cartDiscount.value, subtotalAfterItemDiscounts);
    const qty = item.quantity || 1;
    const itemTotalShare = (afterItemDiscount * qty) / subtotalAfterItemDiscounts;
    const discountForThisItem = cap * itemTotalShare;
    return round2(afterItemDiscount - discountForThisItem / qty);
  }
  return afterItemDiscount;
}

/**
 * Sum of every line item's after-item-discount value (before cart discount).
 * Use as denominator for the proportional cart-discount spread.
 */
export function subtotalAfterItemDiscounts(lineItems) {
  return round2(
    (lineItems || []).reduce(
      (sum, item) => sum + calcItemEffectivePrice(item) * (item.quantity || 1),
      0
    )
  );
}

/**
 * Build the servicesToBook array for the API call on checkout completion.
 * Services added during checkout get serviceTime: 0 so they don't affect diary timing.
 * Cart discount is baked into each price proportionally so the backend's
 * commission calc (totalServiceAmount × salonCommissionRate) reflects the
 * actual sold price.
 *
 * @param {Array} lineItems
 * @param {Object|null} cartDiscount
 * @returns {Array} [{ id, price, serviceTime }]
 */
export function buildServicesToBook(lineItems, cartDiscount = null) {
  const denom = subtotalAfterItemDiscounts(lineItems);
  const result = [];
  lineItems
    .filter(item => item.type === 'service')
    .forEach(item => {
      const qty = item.quantity || 1;
      const price = effectiveUnitPriceAfterCartDiscount(item, cartDiscount, denom);
      for (let i = 0; i < qty; i++) {
        result.push({
          id: item.serviceId || item.id,
          price,
          ...(item.addedDuringCheckout ? { serviceTime: 0 } : {}),
        });
      }
    });
  return result;
}
